import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';

import { SnackbarStack } from './SnackbarStack';
import type { SnackbarAlert } from './SnackbarStack.types';
import type {
  ShowSnackbarOptions,
  SnackbarContextValue,
  SnackbarProviderProps,
} from './SnackbarProvider.types';

/**
 * Counter for generating unique IDs
 */
let idCounter = 0;

/**
 * Generate a unique ID for a snackbar
 */
const generateId = (): string => {
  idCounter += 1;
  return `snackbar-${idCounter}`;
};

/**
 * Context for snackbar functionality
 */
const SnackbarContext = createContext<SnackbarContextValue | null>(null);

/**
 * Hook to access snackbar functionality
 * Must be used within a SnackbarProvider
 *
 * @example
 * ```tsx
 * const { showSnackbar, success, error } = useSnackbar();
 *
 * // Show a custom snackbar
 * showSnackbar({ message: 'Hello!', variant: 'info' });
 *
 * // Convenience methods
 * success('Operation completed!');
 * error('Something went wrong');
 * ```
 */
export const useSnackbar = (): SnackbarContextValue => {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error('useSnackbar must be used within a SnackbarProvider');
  }
  return context;
};

/**
 * SnackbarProvider - Provides snackbar functionality to the entire application
 *
 * Wrap your app with this provider to enable snackbar notifications anywhere
 * using the useSnackbar hook.
 *
 * @example
 * ```tsx
 * // In your app root
 * <SnackbarProvider position="bottom-center">
 *   <App />
 * </SnackbarProvider>
 *
 * // In any component
 * const MyComponent = () => {
 *   const { success, error } = useSnackbar();
 *
 *   const handleSave = async () => {
 *     try {
 *       await saveData();
 *       success('Saved successfully!');
 *     } catch (e) {
 *       error('Failed to save');
 *     }
 *   };
 *
 *   return <button onClick={handleSave}>Save</button>;
 * };
 * ```
 */
export const SnackbarProvider = ({
  children,
  position = 'bottom-center',
  maxSnackbars = 5,
  maxWidth,
  dataTestId,
}: SnackbarProviderProps): ReactElement => {
  const [alerts, setAlerts] = useState<Map<string, SnackbarAlert>>(new Map());
  const alertOrderRef = useRef<string[]>([]);

  /**
   * Show a new snackbar notification
   */
  const showSnackbar = useCallback(
    (options: ShowSnackbarOptions): string => {
      const id = options.id ?? generateId();
      const alert: SnackbarAlert = {
        id,
        message: options.message,
        variant: options.variant ?? 'info',
        action: options.action,
        showCloseButton: options.showCloseButton ?? true,
        autoHideDuration: options.autoHideDuration,
      };

      setAlerts((prev) => {
        const next = new Map(prev);
        next.set(id, alert);

        // Track order for maxSnackbars enforcement
        if (!alertOrderRef.current.includes(id)) {
          alertOrderRef.current.push(id);
        }

        // Remove oldest if we exceed maxSnackbars
        while (next.size > maxSnackbars && alertOrderRef.current.length > 0) {
          const oldestId = alertOrderRef.current.shift();
          if (oldestId) {
            next.delete(oldestId);
          }
        }

        return next;
      });

      return id;
    },
    [maxSnackbars]
  );

  /**
   * Hide a specific snackbar by ID
   */
  const hideSnackbar = useCallback((id: string) => {
    setAlerts((prev) => {
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
    alertOrderRef.current = alertOrderRef.current.filter((alertId) => alertId !== id);
  }, []);

  /**
   * Clear all snackbars
   */
  const clearAll = useCallback(() => {
    setAlerts(new Map());
    alertOrderRef.current = [];
  }, []);

  /**
   * Convenience method to show a success snackbar
   */
  const success = useCallback(
    (message: ReactNode, options?: Omit<ShowSnackbarOptions, 'message' | 'variant'>): string => {
      return showSnackbar({ ...options, message, variant: 'success' });
    },
    [showSnackbar]
  );

  /**
   * Convenience method to show an error snackbar
   */
  const error = useCallback(
    (message: ReactNode, options?: Omit<ShowSnackbarOptions, 'message' | 'variant'>): string => {
      return showSnackbar({ ...options, message, variant: 'error' });
    },
    [showSnackbar]
  );

  /**
   * Convenience method to show a warning snackbar
   */
  const warning = useCallback(
    (message: ReactNode, options?: Omit<ShowSnackbarOptions, 'message' | 'variant'>): string => {
      return showSnackbar({ ...options, message, variant: 'warning' });
    },
    [showSnackbar]
  );

  /**
   * Convenience method to show an info snackbar
   */
  const info = useCallback(
    (message: ReactNode, options?: Omit<ShowSnackbarOptions, 'message' | 'variant'>): string => {
      return showSnackbar({ ...options, message, variant: 'info' });
    },
    [showSnackbar]
  );

  /**
   * Handle snackbar close (called by SnackbarStack)
   */
  const handleClose = useCallback(
    (id: string) => {
      hideSnackbar(id);
    },
    [hideSnackbar]
  );

  const contextValue = useMemo<SnackbarContextValue>(
    () => ({
      showSnackbar,
      hideSnackbar,
      clearAll,
      success,
      error,
      warning,
      info,
    }),
    [showSnackbar, hideSnackbar, clearAll, success, error, warning, info]
  );

  return (
    <SnackbarContext.Provider value={contextValue}>
      {children}
      <SnackbarStack
        alerts={alerts}
        onClose={handleClose}
        position={position}
        maxWidth={maxWidth}
        dataTestId={dataTestId}
      />
    </SnackbarContext.Provider>
  );
};

SnackbarProvider.displayName = 'SnackbarProvider';

export default SnackbarProvider;
